export const sections = [
  {
    id: "basicInformation",
    title: "Basic Information",
    fields: [
      { id: "fullName", label: "What is your full name?", type: "text", placeholder: "First Name Last Name" },
      { id: "email", label: "What is your email address?", type: "email", placeholder: "example@email.com" },
      { id: "phoneNumber", label: "What is your phone number?", type: "text", placeholder: "(###) ###-####" },
      {
        id: "startDate",
        label: "What day does your trip begin?",
        type: "customDate",
      },
      {
        id: "endDate",
        label: "What day will you return home?",
        type: "customDate",
      },
    ],
  },
  {
    id: "groupInfo",
    title: "Group Information",
    fields: [
      {
        id: "adults",
        label: "How many adults (18+) will be traveling with you?",
        type: "dropdown",
        options: Array.from({ length: 11 }, (_, i) =>
          i === 10 ? "10+" : `${i}`
        ),
      },
      {
        id: "numAdultMales",
        label: "How many of those adults identify as male?",
        type: "dropdown",
        dynamicFrom: "adults",
      },
      {
        id: "numAdultFemales",
        label: "How many of those adults identify as female?",
        type: "dropdown",
        dynamicFrom: "adults",
      },
      {
        id: "kids",
        label: "How many children will be in your group?",
        type: "dropdown",
        options: Array.from({ length: 11 }, (_, i) =>
          i === 10 ? "10+" : `${i}`
        ),
      },
      {
        id: "numBoys",
        label: "How many boys?",
        type: "dropdown",
        dynamicFrom: "kids",
      },
      {
        id: "numGirls",
        label: "How many girls?",
        type: "dropdown",
        dynamicFrom: "kids",
      },
      {
        id: "agesOfBoys",
        label: "Please select the age of each boy",
        type: "dynamicAgeDropdown",
        dynamicFrom: "numBoys",
      },
      {
        id: "agesOfGirls",
        label: "Please select the age of each girl",
        type: "dynamicAgeDropdown",
        dynamicFrom: "numGirls",
      },
    ],
  },
  {
    id: "parkTicketAccess",
    title: "Ticket & Park Access",
    fields: [
      {
        id: "ticketType",
        label: "Which type of Disney park ticket will you be using?",
        type: "dropdown",
        options: [
          "Date-Based Ticket",
          "Disney Military Salute Ticket",
          "Annual Pass",
          "Florida Resident Discover Disney Ticket",
          "Sport and Convention tickets",
          "Student group tickets",
          "Other",
        ],
      },
      {
        id: "hasParkHopper",
        label:
          "Does your ticket include Park Hopper access or are you using an Annual Pass?",
        type: "yesno",
      },
      {
        id: "specialEvents",
        label: "Are you planning to attend any special events during your visit? * Please consult with your travel agent for date-specific event availability",
        type: "dropdown",
        options: [
          "No special events planned",
          "Mickey's Not So Scary Halloween Party (Select Dates August 15th - October 31st 2026 (Exact Dates TBD))",
          "Mickey's Very Merry Christmas Party (Select Dates November 7th - December 21st 2026 (Exact Dates TBD))",
          "Disney Jollywood Nights (Select Dates November 8th – December 22nd 2026 (Exact Dates TBD))"
        ],
      },
      {
        id: "parkHopperExplanation",
        label:
          "In the next section, you can select park plans that go beyond the number of total days — for example, visiting multiple parks in one day or planning short visits to different parks",
        type: "info",
        dependsOn: { field: "hasParkHopper", value: "Yes" },
        required: false,
      },
    ],
  },
  {
    id: "parkVisitPlans",
    title: "Park Selection",
    fields: [
      {
        id: "parksInfoNote",
        type: "info",
        label:
          "Did you know?\n\nFrom May 26th through September 8th, 2026, Disney is offering a FREE Water Park ticket for the check-in day of your trip! If you're interested in using it, be sure to mention it to your agent\n\nDisney Springs is a fun destination for shopping, dining, and entertainment — and no ticket is required! Disney buses run regularly to and from Disney Springs from all resorts",
      },
      {
        id: "parks",
        label: "Which Disney parks would you like to visit?",
        type: "multi",
        options: [
          "Magic Kingdom",
          "Hollywood Studios",
          "EPCOT",
          "Animal Kingdom",
          {
            label: "Typhoon Lagoon",
            value: "Typhoon Lagoon",
            openingNoteKeywords: ["May 26, 2026"],
            tooltip: "Typhoon Lagoon is closed starting February 14th, 2026 and expected to reopen May 26th, 2026"
          },
          {
            label: "Blizzard Beach",
            value: "Blizzard Beach",
            openingNoteKeywords: ["February 15, 2026"],
            tooltip: "Blizzard Beach is closed starting September 8, 2025 and expected to reopen February 15th, 2026"
          },
          "Disney Springs",
        ],
      },
      {
        id: "mkDays",
        label: "How many days would you like to spend in Magic Kingdom?",
        dynamicLabel: {
          dependsOn: { field: "hasParkHopper", value: "Yes" },
          trueLabel: "How many times would you like to visit Magic Kingdom?",
          falseLabel: "How many days would you like to spend in Magic Kingdom?"
        },
        type: "dropdown",
        options: Array.from({ length: 10 }, (_, i) => `${i + 1}`),
      },
      {
        id: "hsDays",
        label: "How many days would you like to spend in Hollywood Studios?",
        dynamicLabel: {
          dependsOn: { field: "hasParkHopper", value: "Yes" },
          trueLabel: "How many times would you like to visit Hollywood Studios?",
          falseLabel: "How many days would you like to spend in Hollywood Studios?"
        },
        type: "dropdown",
        options: Array.from({ length: 10 }, (_, i) => `${i + 1}`),
      },
      {
        id: "epcotDays",
        label: "How many days would you like to spend in EPCOT?",
        dynamicLabel: {
          dependsOn: { field: "hasParkHopper", value: "Yes" },
          trueLabel: "How many times would you like to visit EPCOT?",
          falseLabel: "How many days would you like to spend in EPCOT?"
        },
        type: "dropdown",
        options: Array.from({ length: 10 }, (_, i) => `${i + 1}`),
      },
      {
        id: "akDays",
        label: "How many days would you like to spend in Animal Kingdom?",
        dynamicLabel: {
          dependsOn: { field: "hasParkHopper", value: "Yes" },
          trueLabel: "How many times would you like to visit Animal Kingdom?",
          falseLabel: "How many days would you like to spend in Animal Kingdom?"
        },
        type: "dropdown",
        options: Array.from({ length: 10 }, (_, i) => `${i + 1}`),
      },
      {
        id: "tlDays",
        label: "How many days would you like to spend in Typhoon Lagoon?",
        dynamicLabel: {
          dependsOn: { field: "hasParkHopper", value: "Yes" },
          trueLabel: "How many times would you like to visit Typhoon Lagoon?",
          falseLabel: "How many days would you like to spend in Typhoon Lagoon?"
        },
        type: "dropdown",
        options: Array.from({ length: 10 }, (_, i) => `${i + 1}`),
      },
      {
        id: "bbDays",
        label: "How many days would you like to spend in Blizzard Beach?",
        dynamicLabel: {
          dependsOn: { field: "hasParkHopper", value: "Yes" },
          trueLabel: "How many times would you like to visit Blizzard Beach?",
          falseLabel: "How many days would you like to spend in Blizzard Beach?"
        },
        type: "dropdown",
        options: Array.from({ length: 10 }, (_, i) => `${i + 1}`),
      },
      {
        id: "includeRestDays",
        label: "Would you like to include Rest days in your trip?",
        type: "yesno",
      },
      {
        id: "restDayCount",
        label: "How many Rest day(s) would you like?",
        type: "dropdown",
        options: Array.from({ length: 7 }, (_, i) => `${i + 1}`),
        dependsOn: { field: "includeRestDays", value: "Yes" },
        required: false,
      },
      {
        id: "restDayPlacement",
        label: "Where would you like your Rest day(s) to fall?",
        type: "dropdown",
        options: [
          "Beginning of trip",
          "Middle of trip",
          "End of trip",
        ],
        dependsOn: { field: "includeRestDays", value: "Yes" },
        required: false,
      },
      {
        id: "restDayStyle",
        label: "What should a Rest day(s) look like?",
        type: "dropdown",
        options: [
          "True Rest (no parks)",
          "Light Park Day (easy pace)",
        ],
        dependsOn: { field: "includeRestDays", value: "Yes" },
        required: false,
      },
      {
        id: "finalParkPreference",
        label: "Would you like to end your trip at a specific park?",
        type: "dropdown",
        options: [
          "No Preference",
          "Magic Kingdom",
          "Hollywood Studios",
          "EPCOT",
          "Animal Kingdom",
        ],
      },
    ],
  },
  {
    id: "accommodation",
    title: "Accommodation & Transportation",
    fields: [
      {
        id: "resortsInfoNote",
        type: "info",
        label:
          "Want quick and scenic access to EPCOT and Hollywood Studios?\nResorts with direct access to the Disney Skyliner include:\n- Disney's Riviera Resort (Deluxe)\n- Disney's Caribbean Beach Resort (Moderate)\n- Disney's Art of Animation Resort (Value)\n- Disney's Pop Century Resort (Value)\n\n\nPrefer convenient Monorail access to Magic Kingdom and EPCOT?\nMonorail-connected resorts include:\n- Disney's Contemporary Resort (Deluxe)\n- Disney's Polynesian Village Resort (Deluxe)\n- Disney's Grand Floridian Resort & Spa (Deluxe)",
      },
      {
        id: "transportation",
        label: "How will you get around the Disney Parks?",
        type: "dropdown",
        options: [
          "Complimentary Disney Transportation (Disney Bus, SkyLiner, Monorail)",
          "Paid Disney Transportatin (e.g. Minnie Van)",
          "Driving",
          "All of the above",
        ],
      },
      {
        id: "stayingOnProperty",
        label: "Where will you be staying this trip?",
        type: "dropdown",
        options: [
          "Deluxe Resorts",
          "Deluxe Villas",
          "Moderate Resorts",
          "Value Resorts",
          "Campgrounds",
          "Off Disney Property",
        ],
      },
      {
        id: "resort",
        type: "dynamicResortDropdown",
      },
      {
        id: "otherHotel",
        label: "Please specify your off-property hotel name",
        dependsOn: { field: "stayingOnProperty", value: "Off Disney Property" },
        type: "text",
      },
    ],
  },
  {
    id: "attractionRideStrategy",
    title: "Attraction & Ride Preferences",
    fields: [
      {
        id: "lightningLane",
        label:
          "Do you plan to use Lightning Lane Multi-Pass (formerly Genie+)?",
        type: "single",
        options: ["Yes", "No", "Not sure yet"],
      },
      {
        id: "rideStrategy",
        label: "What best describes your ride strategy?",
        type: "checkbox",
        options: [
          "Thrill Seeker – Prioritize coasters & big rides",
          "Family-Friendly – Mix of thrill rides & kid-friendly attractions",
          "Young Children – Slow rides & character experiences",
          "Go With the Flow – Prioritize shows & dining",
          "Park Completionist – Ride everything possible",
          "All Rides – You Choose!",
        ],
      },
      {
        id: "rideKeyInfo",
        type: "info",
        label:
          "Key:\n? = General Ride Information (includes ride descriptions and height requirements)\n! = Ride Closure Information",
      },
      {
        id: "mustRides",
        type: "dynamicRideDropdown",
      },
    ],
  },
  {
    id: "showFireworkStrategy",
    title: "Shows & Firework Preferences",
    fields: [
      {
        id: "showStrategy",
        label: "What best describes your show & entertainment interests?",
        type: "checkbox",
        options: [
          "Nighttime Spectaculars – Fireworks, projections, and big finales",
          "Stage Shows – Broadway-style performances & musical revues",
          "Animal & Nature – Live animal shows and educational experiences",
          "Cultural – World Showcase and themed entertainment",
          "Parades – Daytime or evening character cavalcades and processionals",
          "See it All – No preference, show me everything",
          "Not Interested",
        ],
      },
      {
        id: "mustSeeShows",
        type: "dynamicShowDropdown",
      },
      {
        id: "fantasmicDiningPackageInterest",
        label: "Since you selected Fantasmic!, would you be interested in the Fantasmic! Dining Package (includes reserved seating for the show)?",
        type: "single",
        options: ["Yes", "No", "Not Sure Yet"],
        dependsOn: { field: "mustSeeShows", value: "[includes:Fantasmic!]" },
      },
      {
        id: "fantasmicDiningPackageRestaurants",
        label: "If yes, which restaurant(s) would you prefer for the Fantasmic! Dining Package? (Select all that apply)",
        type: "multi",
        options: [
          "50's Prime Time Cafe",
          "Hollywood & Vine (Breakfast, Lunch, or Dinner)",
          "Sci-Fi Dine-In Theater Restaurant",
          "The Hollywood Brown Derby",
        ],
        dependsOn: { field: "fantasmicDiningPackageInterest", value: "Yes" },
      },
    ],
  },
  {
    id: "specialtyExperiences",
    title: "Specialty/Premium Experiences",
    fields: [
      {
        id: "enchantedExperiences",
        label: "Are you interested in any enchanted experiences?",
        type: "single",
        options: [
          "Yes",
          "No", 
          "Not Sure Yet"
        ],
      },
      {
        id: "mustExperience",
        label: "If yes, which premium experiences interest you?",
        type: "dynamic",
        dependsOn: { field: "enchantedExperiences", value: "Yes" },
        component: "ExperienceSelector",
      },
    ],
  },
  {
    id: "characterMeetGreets",
    title: "Character Meet & Greets",
    fields: [
      {
        id: "interestedInCharacterMeetGreets",
        label: "Are you interested in any Character Meet & Greets during your trip?",
        type: "single",
        options: ["Yes", "No", "Not Sure Yet"],
      },
      {
        id: "mustMeetCharacters",
        label: "If yes, which characters would you like to meet? (Select one or more)",
        type: "multiselect",
        options: [],
        dependsOn: { field: "interestedInCharacterMeetGreets", value: "Yes" },
      },
      // mustMeetCharactersOther is shown inline in ModernFieldRenderer when "Other" is selected
    ],
  },
  {
    id: "diningPreferences",
    title: "Dining Preferences",
    fields: [
      {
        id: "diningPlanInterest",
        label: "Are you interested in using a Disney Dining Plan?",
        type: "yesno",
      },
      {
        id: "diningPlanInfo",
        type: "info",
        label:
          "💲 = Budget ($) – up to $15 per person\n💲💲 = Moderate ($$) – $15–$35 per person\n💲💲💲 = Fine Dining ($$$) – $35–$60 per person\n💲💲💲💲 = Signature Dining ($$$$) – $60+ per person",
      },
      {
        id: "restaurantStrategy",
        label: "What best describes your restaurant preferences?",
        type: "checkbox",
        options: [
          "Character Dining – Meals with character meet-and-greets",
          "Signature Dining – Upscale / “Special Night” Restaurants",
          "Themed Dining – Immersive restaurants for atmosphere and fun",
          "Quick Service – Counter service, mobile order, fast-casual",
          "See it All – No preference, show me everything",
        ],
      },
      {
        id: "mustTryRestaurants",
        type: "dynamicRestaurantDropdown",
      },
      {
        id: "diningTime",
        label: "What time of day do you prefer to dine?",
        type: "multi",
        options: [
          "Early (Before 11:30 AM / Before 5:00 PM)",
          "Normal (11:30 AM - 1:30 PM / 5:00 PM - 7:00 PM)",
          "Late (After 1:30 PM / After 7:00 PM)",
          "No preference",
        ],
      },
      {
        id: "allergies",
        label: "Do you or anyone in your group have any food allergies?",
        type: "multi",
        options: [
          "None",
          "Peanuts",
          "Tree Nuts",
          "Dairy",
          "Gluten",
          "Shellfish",
          "Soy",
          "Eggs",
          "Other",
        ],
        required: true,
      },
    ],
  },
  {
    id: "timeEnergy",
    title: "Time & Energy Preferences",
    fields: [
      {
        id: "resortBenefitsNote",
        label: "Resort Benefits Information",
        type: "resortBenefitsNote",
        required: false,
      },
      {
        id: "parkStartTime",
        label: "When do you prefer to enter the parks each day?",
        type: "multi",
        options: [
          "Rope Drop (Before park opening)",
          "Mid-Morning (9:30 AM - 11:00 AM)",
          "Afternoon (12:00 PM - 3:00 PM)",
          "Evening (After 4:00 PM)",
        ],
      },
      {
        id: "breakTime",
        label: "Do you plan to take a break during your park day?",
        type: "multi",
        options: [
          "No Break – Full Park Day",
          "Midday Hotel Break",
          "Afternoon Park Break",
          "Evening Break",
        ],
      },
    ],
  },
  {
    id: "specialConsiderations",
    title: "Special Considerations",
    fields: [
      {
        id: "celebration",
        label: "Are you celebrating anything special during your trip?",
        type: "single",
        options: ["Yes", "No"],
      },
      {
        id: "celebrationDetails",
        label: "If yes, please tell us what you're celebrating:",
        type: "dropdown",
        options: [
          "Birthday",
          "Anniversary",
          "Graduation",
          "Other",
        ],
        dependsOn: { field: "celebration", value: "Yes" },
      },
      {
        id: "celebrationDate",
        label: "What is the date of the celebration?",
        type: "customDate",
        dependsOn: { field: "celebration", value: "Yes" },
      },
      {
        id: "celebrationOtherDetails",
        label: "What specifically are you celebrating?",
        type: "text",
        placeholder: "e.g., retirement, first visit, engagement, etc.",
        dependsOn: { field: "celebrationDetails", value: "Other" },
      },
      {
        id: "hasDisability",
        label:
          "Does anyone in your group have a disability or mobility concern?",
        type: "single",
        options: ["Yes", "No"],
      },
      {
        id: "disabilities",
        label: "If yes, please explain so we can accommodate your needs:",
        type: "text",
        dependsOn: { field: "hasDisability", value: "Yes" },
        placeholder: "e.g., wheelchair access needed, mobility scooter rental, hearing assistance, etc.",
      },
      {
        id: "additionalInfo",
        label:
          "Is there anything else we should know about your travel preferences or needs?",
        type: "single",
        options: ["Yes", "No"],
      },
      {
        id: "additionalInfoDetails",
        label: "If yes, please tell us:",
        type: "text",
        dependsOn: { field: "additionalInfo", value: "Yes" },
        placeholder: "e.g., We'd love to meet Mickey if possible!",
      },
    ],
  },
];